import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, Mail01, Phone01, ProgressBar } from "@opendate-app/setlist";

const styles = {
  aside: { flex: "0 1 320px", minWidth: 280, position: "sticky", top: 24 },
  summary: { padding: "20px 20px 16px", display: "flex", flexDirection: "column", gap: 4 },
  label: { fontSize: 14, lineHeight: "20px", fontWeight: 600, color: "var(--sl-text-secondary, #414651)" },
  countRow: { display: "flex", alignItems: "baseline", gap: 8, transition: "opacity 200ms ease" },
  count: {
    fontSize: 36,
    lineHeight: "44px",
    fontWeight: 600,
    letterSpacing: "-0.02em",
    color: "var(--sl-text-primary, #333333)",
    fontVariantNumeric: "tabular-nums",
  },
  unit: { fontSize: 14, lineHeight: "20px", color: "var(--sl-text-tertiary, #535862)" },
  of: { fontSize: 12, lineHeight: "18px", color: "var(--sl-text-quaternary, #717680)", marginBottom: 8 },
  channels: { display: "grid", gridTemplateColumns: "1fr 1fr", borderTop: "1px solid var(--sl-border-secondary, #E9EAEB)" },
  channel: { padding: "12px 20px", display: "flex", flexDirection: "column", gap: 2 },
  channelLabel: { display: "flex", alignItems: "center", gap: 6, fontSize: 12, lineHeight: "18px", color: "var(--sl-text-tertiary, #535862)" },
  channelCount: { fontSize: 16, lineHeight: "24px", fontWeight: 600, color: "var(--sl-text-primary, #333333)", fontVariantNumeric: "tabular-nums" },
};

const formatCount = (count) => (count ?? 0).toLocaleString("en-US");

const AudienceEstimate = ({ team, fanFilterGroupId, refreshKey, building = false, contactLabel = "Fans" }) => {
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!fanFilterGroupId) return undefined;

    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      axios.get(`/teams/${team.id}/fan_filter_groups/${fanFilterGroupId}/audience_estimate`)
        .then(({ data }) => {
          if (cancelled) return;
          setEstimate(data);
          setFailed(false);
        })
        .catch(() => {
          if (cancelled) return;
          setEstimate(null);
          setFailed(true);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [team.id, fanFilterGroupId, refreshKey]);

  const noun = contactLabel.toLowerCase();
  const percent = estimate?.total ? (estimate.fans / estimate.total) * 100 : 0;

  return (
    <aside style={styles.aside} aria-label="Estimated audience" aria-busy={loading || building ? "true" : undefined}>
      <Card padding="none">
        <div style={styles.summary}>
          <span style={styles.label}>Estimated audience</span>
          <div style={{ ...styles.countRow, opacity: loading || building ? 0.35 : 1 }}>
            <span style={styles.count}>{estimate ? formatCount(estimate.fans) : "—"}</span>
            <span style={styles.unit}>{noun}</span>
          </div>
          <span style={styles.of}>
            {estimate
              ? `of ${formatCount(estimate.total)} ${noun} · ${Math.round(percent)}%`
              : failed ? `Couldn't count ${noun} right now.` : `Counting your ${noun}…`}
          </span>
          <ProgressBar value={percent} />
        </div>
        <div style={styles.channels}>
          <div style={{ ...styles.channel, borderRight: "1px solid var(--sl-border-secondary, #E9EAEB)" }}>
            <span style={styles.channelLabel}><Mail01 width={14} height={14} aria-hidden="true" />Email</span>
            <span style={styles.channelCount}>{estimate ? formatCount(estimate.email) : "—"}</span>
          </div>
          <div style={styles.channel}>
            <span style={styles.channelLabel}><Phone01 width={14} height={14} aria-hidden="true" />SMS</span>
            <span style={styles.channelCount}>{estimate ? formatCount(estimate.sms) : "—"}</span>
          </div>
        </div>
      </Card>
    </aside>
  );
};

export default AudienceEstimate;
